import {
  FloatingNode,
  FloatingPortal,
  FloatingTree,
  Placement,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  size,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useHover,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react";
import React, {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { ZIndex } from "@/constants";
import { useDialogLayer } from "@/components/organisms/Dialog";
import { useDevice } from "@/hooks/useDevice";
import {
  isEventInsideContext,
  useContextOverlay,
} from "@/hooks/useContextTree";

/**
 * What a popper's search box (``List.SearchInput``) is told by the popper it
 * is in, and tells it. On a phone a popper with a search box is a sheet over
 * the whole screen - its box at the top, what it finds under it down to the
 * keyboard - rather than a box under what opened it: a field low on the page
 * left its list a strip between itself and the keyboard, and iOS slid the
 * page about to bring the field into view.
 */
export const PopperSheetContext = createContext<{
  isSheet: boolean;
  setHasSearch: (hasSearch: boolean) => void;
  close: () => void;
} | null>(null);

interface PopperProps {
  children: React.ReactNode;
  anchor: React.ReactNode;
  placement?: Placement;
  mainAxisOffset?: number;
  crossAxisOffset?: number;
  isOpenState?: [boolean, (isOpen: boolean) => void];
  isEscapeable?: boolean;
  isClickable?: boolean;
  isFocusable?: boolean;
  isHoverable?: boolean;
  /** When true (default), focus the first focusable child on open. */
  autoFocus?: boolean;
  style?: React.CSSProperties;
  floatingStyle?: React.CSSProperties;
  forceHide?: boolean;
  hoverOpenDelay?: number;
  hoverCloseDelay?: number;
  /**
   * ``button-group``: the anchor is one of a ``ButtonGroup``'s own - it is
   * the reference itself, given the popper's handlers, with nothing round
   * it: the group squares and joins its children by its own selectors
   * (``[&>*:not(:first-child)]``), which reach no button a wrapper holds.
   */
  anchorLayout?: "default" | "button-group";
  /** Restore focus to this element when the popper closes (e.g. the + button). */
  focusRestoreRef?: React.RefObject<HTMLElement | null>;
  /**
   * When ``none``, Escape closes without ``focusParent`` so
   * ``focusRestoreRef`` can restore on teardown (see ``useContextOverlay``).
   */
  restoreFocusOnEscape?: "parent" | "none";
  /**
   * Extra outside-press guard. Context-tree nesting is always respected;
   * this prop can only further cancel (or allow) dismissal after that check.
   */
  outsidePress?: boolean | ((event: MouseEvent) => boolean);
  /** Data attributes for the element the anchor sits in - in a button group, the anchor's own. */
  [attribute: `data-${string}`]: unknown;
}

function Inner({
  children,
  anchor,
  placement = "bottom-start",
  mainAxisOffset = 4,
  crossAxisOffset = 0,
  isOpenState,
  isEscapeable = true,
  isClickable = true,
  isFocusable = false,
  isHoverable = false,
  autoFocus = true,
  floatingStyle,
  style,
  forceHide = false,
  hoverOpenDelay = 100,
  hoverCloseDelay = 250,
  anchorLayout = "default",
  focusRestoreRef,
  restoreFocusOnEscape = "parent",
  outsidePress = true,
  ...attributes
}: PopperProps) {
  const nodeId = useFloatingNodeId();
  const dialogLayer = useDialogLayer();
  const popperZIndex =
    dialogLayer > 0
      ? ZIndex.APP.DIALOG + (dialogLayer - 1) * 200 + 100
      : ZIndex.APP.POPPER;

  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = isOpenState ? isOpenState[0] : internalOpen;
  const setIsOpen = isOpenState ? isOpenState[1] : setInternalOpen;

  const effectiveOpen = isOpen && !forceHide;
  const isHoverOnly = isHoverable && !isClickable && !isFocusable;

  // A sheet on a phone, when what it holds is searched (see
  // ``PopperSheetContext``): as tall as the room above the keyboard, and where
  // that room is - the visual viewport, followed as the keyboard comes and
  // goes. Zoomed in by a pinch, the screen's own height.
  const { isMobile } = useDevice();
  const [hasSearch, setHasSearch] = useState(false);
  const isSheet = isMobile && hasSearch && !isHoverOnly;
  const isSheetRef = useRef(isSheet);
  isSheetRef.current = isSheet;
  const [sheetBox, setSheetBox] = useState<{
    top: number;
    height: number;
  } | null>(null);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!isSheet || !effectiveOpen || !viewport) return;
    const fit = () =>
      setSheetBox(
        viewport.scale > 1.01
          ? null
          : { top: viewport.offsetTop, height: viewport.height },
      );
    fit();
    viewport.addEventListener("resize", fit);
    viewport.addEventListener("scroll", fit);
    return () => {
      viewport.removeEventListener("resize", fit);
      viewport.removeEventListener("scroll", fit);
    };
  }, [effectiveOpen, isSheet]);

  const openedByClickRef = useRef(false);

  // ── Positioning via floating-ui ────────────────────────────────────────────
  const middleware = [
    shift({ padding: 32 }),
    flip(),
    offset({ mainAxis: mainAxisOffset, crossAxis: crossAxisOffset }),
    size({
      padding: 32,
      apply({ availableHeight, elements }) {
        // A sheet is as tall as the screen's room, not the room by its anchor.
        if (isSheetRef.current) {
          elements.floating.style.removeProperty("max-height");
          return;
        }
        // Cap the popper to the viewport-available height but do NOT
        // make it scroll. The popper is a flex column (see
        // ``portalContentStyle`` below) so a single ``flex: 1;
        // min-height: 0`` child claims this height and owns scrolling
        // itself. Setting ``overflowY: auto`` here too creates a
        // double-scroll: an outer popper scrollbar that fights with
        // the inner List's flex layout and pushes pinned footers /
        // "Create new" buttons out of view when the popper is squashed
        // near the viewport edge.
        elements.floating.style.maxHeight = `${availableHeight}px`;
      },
    }),
  ];

  const { context, refs, floatingStyles } = useFloating({
    nodeId,
    open: effectiveOpen,
    onOpenChange: (open, _event, reason) => {
      if (!open && reason === "hover" && openedByClickRef.current) return;
      if (!open) {
        openedByClickRef.current = false;
      }
      setIsOpen(open && !forceHide);
    },
    middleware,
    placement: placement || undefined,
    whileElementsMounted: autoUpdate,
    // Placed by its top and left, so its transform is free to open it with.
    transform: false,
  });

  // It opens out of what it was opened from: faded in, come the last few
  // pixels from that side and a touch grown - and goes the same way, a little
  // quicker. A tooltip only fades. The app's own durations and its input
  // easing; none at all where motion is turned down.
  const reducesMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: reducesMotion
      ? 0
      : isHoverOnly
        ? { open: 80, close: 80 }
        : { open: 150, close: 100 },
    // A sheet comes up from below, as a phone's do.
    initial: ({ side }) =>
      isSheetRef.current
        ? { opacity: 0, transform: "translateY(12px)" }
        : isHoverOnly
          ? { opacity: 0 }
          : {
              opacity: 0,
              transform: `translate(${
                { left: "4px", right: "-4px" }[side as string] ?? "0"
              }, ${
                { top: "4px", bottom: "-4px" }[side as string] ?? "0"
              }) scale(0.98)`,
            },
    // Grown from the edge, and the end, nearest what it was opened from.
    common: ({ side, placement: placed }) => {
      const along = side === "top" || side === "bottom";
      const align = placed.split("-")[1];
      return {
        transformOrigin: `${
          along
            ? ({ start: "left", end: "right" }[align ?? ""] ?? "center")
            : { left: "right", right: "left" }[side]
        } ${
          along
            ? { top: "bottom", bottom: "top" }[side]
            : ({ start: "top", end: "bottom" }[align ?? ""] ?? "center")
        }`,
        transitionTimingFunction: "cubic-bezier(0.33, 1, 0.68, 1)",
      };
    },
  });

  // ── Overlay focus management (trapping + hotkey isolation + escape + auto-focus + restore) ──
  const shouldManageFocus = !isHoverOnly;
  const { Scope, node: overlayNode } = useContextOverlay({
    isOpen: effectiveOpen && shouldManageFocus,
    onClose: () => setIsOpen(false),
    escapeable: isEscapeable,
    autoFocus: autoFocus && shouldManageFocus,
    focusRestoreRef:
      focusRestoreRef ??
      (refs.reference as React.RefObject<HTMLElement | null>),
    restoreFocusOnEscape,
  });

  // ── Hover + outside-press dismiss via floating-ui ──────────────────────────
  const hoverInteraction = useHover(context, {
    enabled: isHoverable,
    restMs: hoverOpenDelay,
    delay: { open: 0, close: hoverCloseDelay },
    handleClose: safePolygon({ requireIntent: false, buffer: 0 }),
    move: false,
  });

  const dismissInteraction = useDismiss(context, {
    outsidePress: (event) => {
      const target = event.target;
      if (target instanceof Element) {
        const portalRoot = target.closest("[data-floating-ui-portal]");
        if (portalRoot?.contains(refs.floating.current)) {
          return false;
        }
      }
      // Nested portaled overlays share our context tree even when they are
      // DOM siblings under the portal root — treat them as inside.
      if (isEventInsideContext(event, overlayNode)) return false;
      if (typeof outsidePress === "function") return outsidePress(event);
      return outsidePress !== false;
    },
    escapeKey: false,
    bubbles: { outsidePress: false },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hoverInteraction,
    dismissInteraction,
  ]);

  // ── forceHide ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (forceHide && isOpen) setIsOpen(false);
  }, [forceHide, isOpen, setIsOpen]);

  // ── Click handler ──────────────────────────────────────────────────────────
  const handleReferenceClick = useCallback(() => {
    if (!isClickable) return;
    if (isOpen) {
      setIsOpen(false);
    } else {
      openedByClickRef.current = true;
      setIsOpen(true);
    }
  }, [isClickable, isOpen, setIsOpen]);

  // ── Focus handler ──────────────────────────────────────────────────────────
  const handleReferenceFocus = useCallback(() => {
    if (!isFocusable || isOpen) return;
    setIsOpen(true);
  }, [isFocusable, isOpen, setIsOpen]);

  // ── Styles ─────────────────────────────────────────────────────────────────
  const portalContentStyle: React.CSSProperties = {
    overscrollBehavior: "contain",
    cursor: "default",
    pointerEvents: "auto",
    border: `1px solid ${"var(--border)"}`,
    borderRadius: "calc(var(--radius) - 2px)",
    boxShadow: "var(--shadow-sm)",
    backgroundColor: "var(--popover)",
    overflow: "hidden",
    // Make the popper a vertical flex container so children using
    // ``flex: 1; min-height: 0`` can claim the (size-middleware-
    // capped) ``maxHeight`` exactly. Without this, children fall back
    // to their own intrinsic size — which is why fixed
    // ``maxHeight: 417px`` worked on roomy viewports but cropped the
    // footer / "Create new" button when the popper was squashed.
    display: "flex",
    flexDirection: "column",
    ...floatingStyle,
  };
  const sheetStyle: React.CSSProperties = {
    position: "fixed",
    left: 0,
    right: 0,
    top: sheetBox?.top ?? 0,
    ...(sheetBox ? { height: sheetBox.height } : { bottom: 0 }),
    width: "auto",
    maxHeight: "none",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    paddingTop: "env(safe-area-inset-top)",
    paddingBottom: "env(safe-area-inset-bottom)",
    boxSizing: "border-box",
  };

  const portal = (
    <FloatingPortal>
      {isMounted && (
        <Scope
          ref={refs.setFloating}
          {...getFloatingProps({
            // A press in here is the popper's. Drawn in a portal, it still
            // goes up React's tree to whatever opened it - a card's
            // control that holds focus by taking a press's default away,
            // a row that starts a swipe - and a field in here could not be
            // pressed into (on a phone, which focuses no field it was not
            // tapped on, not at all). Given here, so the popper's own
            // handlers for these stay beside them.
            onMouseDown: (event) => event.stopPropagation(),
            onPointerDown: (event) => event.stopPropagation(),
            onTouchStart: (event) => event.stopPropagation(),
          })}
          // What a sheet it was opened in reads as its own: a press in
          // here does not shut it (``onInteractOutside``).
          data-popper=""
          tabIndex={isHoverOnly ? -1 : 0}
          style={{
            width: "max-content",
            position: "relative",
            willChange: "transform",
            zIndex: popperZIndex,
            ...floatingStyles,
            outline: "none",
            ...portalContentStyle,
            ...transitionStyles,
            ...(isSheet && sheetStyle),
            // Going, it is past pressing.
            ...(!effectiveOpen && { pointerEvents: "none" }),
          }}
        >
          <PopperSheetContext.Provider
            value={{ isSheet, setHasSearch, close: () => setIsOpen(false) }}
          >
            {children}
          </PopperSheetContext.Provider>
        </Scope>
      )}
    </FloatingPortal>
  );

  if (anchorLayout === "button-group" && isValidElement(anchor)) {
    const own = anchor.props as Record<string, unknown> & {
      onClick?: (event: React.MouseEvent) => void;
      onFocus?: (event: React.FocusEvent) => void;
    };
    return (
      <FloatingNode id={nodeId}>
        {cloneElement(anchor as React.ReactElement<Record<string, unknown>>, {
          ...attributes,
          // Its own handlers given with the popper's, so each runs.
          ...getReferenceProps({
            ...own,
            onClick: (event: React.MouseEvent) => {
              own.onClick?.(event);
              handleReferenceClick();
            },
            onFocus: (event: React.FocusEvent) => {
              own.onFocus?.(event);
              handleReferenceFocus();
            },
          }),
          ref: refs.setReference,
        })}
        {portal}
      </FloatingNode>
    );
  }

  return (
    <div
      {...attributes}
      style={{
        maxWidth: "100%",
        width: "fit-content",
        display: "inline-block",
        ...style,
      }}
    >
      <FloatingNode id={nodeId}>
        <div
          ref={refs.setReference}
          {...getReferenceProps({
            onClick: handleReferenceClick,
            onFocus: handleReferenceFocus,
          })}
          style={{
            borderRadius: "calc(var(--radius) - 4px)",
            height: "100%",
          }}
        >
          {anchor}
        </div>
        {portal}
      </FloatingNode>
    </div>
  );
}

export function Popper(props: PopperProps) {
  const parentId = useFloatingParentNodeId();

  if (parentId === null) {
    return (
      <FloatingTree>
        <Inner {...props} />
      </FloatingTree>
    );
  }

  return <Inner {...props} />;
}
